import React from 'react';
import { Button, Checkbox } from '@mantine/core';
import type { VariantComboGroupView } from '../../../../services/variants-combos/variants-combos.service';
import classes from './pos-limit-items-section.module.scss';

type Props = {
  groups: VariantComboGroupView[];
  isManager: boolean;
  interactionsLocked: boolean;
  selectedItemIds: Set<number>;
  onToggleSelected: (itemId: number, checked: boolean) => void;
  onSetInStock: (itemIds: number[], inStock: boolean) => void;
};

const hasOutOfStockItem = (item: VariantComboGroupView['items'][number]): boolean =>
  item.inStock === false || item.childGroups.some(hasOutOfStockGroup);

const hasOutOfStockGroup = (group: VariantComboGroupView): boolean => group.items.some(hasOutOfStockItem);

export const VariantComboStockRows: React.FC<Props> = ({
  groups,
  isManager,
  interactionsLocked,
  selectedItemIds,
  onToggleSelected,
  onSetInStock,
}) => (
  <div className={classes.optionTree} data-testid="limit-items-option-tree" onClick={(event) => event.stopPropagation()}>
    {groups.map((group) => (
      <GroupRows
        key={group.id}
        group={group}
        isManager={isManager}
        interactionsLocked={interactionsLocked}
        selectedItemIds={selectedItemIds}
        onToggleSelected={onToggleSelected}
        onSetInStock={onSetInStock}
      />
    ))}
  </div>
);

const GroupRows: React.FC<Omit<Props, 'groups'> & { group: VariantComboGroupView }> = ({ group, ...props }) => {
  if (!props.isManager && !hasOutOfStockGroup(group)) {
    return null;
  }

  return (
    <div className={classes.optionGroup}>
      <span className={classes.optionGroupName}>{group.name}</span>
      {group.items.map((item) => (
      <React.Fragment key={item.id}>
        {(props.isManager || item.inStock === false) && (
          <div
            className={`${classes.optionRow} ${item.inStock === false ? classes.optionRowOutOfStock : classes.optionRowInStock} ${props.isManager ? classes.optionRowInteractive : ''}`}
            role={props.isManager ? 'button' : undefined}
            tabIndex={props.isManager && !props.interactionsLocked ? 0 : undefined}
            onClick={props.isManager && !props.interactionsLocked ? () => props.onSetInStock([item.id], item.inStock === false) : undefined}
            onKeyDown={
              props.isManager && !props.interactionsLocked
                ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      props.onSetInStock([item.id], item.inStock === false);
                    }
                  }
                : undefined
            }
          >
            {props.isManager && (
              <span onClick={(event) => event.stopPropagation()}>
                <Checkbox
                  data-testid={`limit-items-option-select-${item.id}`}
                  checked={props.selectedItemIds.has(item.id)}
                  onChange={(event) => props.onToggleSelected(item.id, event.currentTarget.checked)}
                  aria-label={`בחר ${item.name}`}
                  disabled={props.interactionsLocked}
                />
              </span>
            )}
            <span className={classes.optionName}>{item.name}</span>
            {props.isManager && (
              <Button
                type="button"
                data-testid={`limit-items-option-stock-${item.id}`}
                className={`${classes.optionStockButton} ${item.inStock === false ? classes.optionStockButtonRestore : classes.optionStockButtonRemove}`}
                onClick={(event) => {
                  event.stopPropagation();
                  props.onSetInStock([item.id], item.inStock === false);
                }}
                disabled={props.interactionsLocked}
              >
                {item.inStock === false ? 'החזר למלאי' : 'הסר ממלאי'}
              </Button>
            )}
          </div>
        )}
        {item.childGroups.map((childGroup) => <GroupRows key={childGroup.id} group={childGroup} {...props} />)}
      </React.Fragment>
      ))}
    </div>
  );
};